import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  MessageSquare, X, Send, Search, Users, Shield, Globe, Smile, ChevronLeft,
  Loader2, Bell, BellOff, UserCircle, Paperclip, Settings, Trash2, UserPlus, UserMinus, Edit3, Pencil, Lock, Check, CheckCheck
} from 'lucide-react';
import { EMOJI_CATS, getRoleLabel, Modal, confirmDialog } from '../shared';

export default function ChatSystem({ user, authHeaders, API_URL, isAdminOrCoach, isSponsorOrAbove }) {
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [readReceipts, setReadReceipts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiCat, setEmojiCat] = useState('Smileys');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [totalUnread, setTotalUnread] = useState(0);
  const [notifSettings, setNotifSettings] = useState({ dm: true, wolfpack: true, team: true, community: true, global: true });
  const [showSettings, setShowSettings] = useState(false);
  const [teamConvs, setTeamConvs] = useState([]);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifSearch, setGifSearch] = useState('');
  const [gifResults, setGifResults] = useState([]);
  const [gifLoading, setGifLoading] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [groupSearchResults, setGroupSearchResults] = useState([]);
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [editGroupName, setEditGroupName] = useState('');
  const [groupMemberSearch, setGroupMemberSearch] = useState('');
  const [groupMemberResults, setGroupMemberResults] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [pastePreview, setPastePreview] = useState(null);
  const [chatToast, setChatToast] = useState(null);
  const gifSearchTimer = useRef(null);
  const fileInputRef = useRef(null);
  const toastTimer = useRef(null);

  const showToast = (msg, type = 'error') => {
    setChatToast({ msg, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setChatToast(null), 4000);
  };
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const authHeadersRef = useRef(authHeaders);
  useEffect(() => { authHeadersRef.current = authHeaders; }, [authHeaders]);

  const getHeaders = () => {
    const token = localStorage.getItem('community_token');
    return token ? { Authorization: `Bearer ${token}` } : authHeadersRef.current;
  };

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const hdrs = getHeaders();
      const [convResp, teamResp, notifResp] = await Promise.all([
        axios.get(`${API_URL}/api/community/chat/conversations`, { headers: hdrs }),
        axios.get(`${API_URL}/api/community/chat/team-conversations`, { headers: hdrs }),
        axios.get(`${API_URL}/api/community/chat/notification-settings`, { headers: hdrs }),
      ]);
      setConversations(convResp.data.conversations || []);
      setTeamConvs(teamResp.data.teams || []);
      setNotifSettings(notifResp.data.settings || {});
      const total = (convResp.data.conversations || []).reduce((sum, c) => sum + (c.unread || 0), 0);
      setTotalUnread(total);
    } catch (e) { console.error(e); }
  }, [API_URL]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Polling-based notifications (replaces WebSocket which doesn't work through ingress)
  const notifSettingsRef = useRef(notifSettings);
  useEffect(() => { notifSettingsRef.current = notifSettings; }, [notifSettings]);
  const lastPollRef = useRef(null);
  const activeConvRef = useRef(null);
  useEffect(() => { activeConvRef.current = activeConv; }, [activeConv]);

  useEffect(() => {
    if (!user) return;
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    const poll = async () => {
      try {
        const hdrs = getHeaders();
        const resp = await axios.get(`${API_URL}/api/community/chat/conversations`, { headers: hdrs });
        const newConvs = resp.data.conversations || [];
        const prevConvs = lastPollRef.current;

        if (prevConvs) {
          // Check for new messages by comparing unread counts
          for (const nc of newConvs) {
            const pc = prevConvs.find(c => c.id === nc.id);
            const prevUnread = pc?.unread || 0;
            const newUnread = nc.unread || 0;
            if (newUnread > prevUnread && nc.last_message_author) {
              // New message detected - show browser notification
              const ns = notifSettingsRef.current;
              if (ns.global !== false && 'Notification' in window && Notification.permission === 'granted') {
                if (ns[nc.type] !== false) {
                  new Notification(nc.display_name || nc.last_message_author, {
                    body: nc.last_message_text?.substring(0, 80) || 'Neue Nachricht',
                    icon: nc.display_image ? `${API_URL}${nc.display_image}` : undefined,
                    tag: `chat-${nc.id}`,
                  });
                }
              }
              // If this conversation is currently open, reload messages
              if (activeConvRef.current?.id === nc.id) {
                try {
                  const msgResp = await axios.get(`${API_URL}/api/community/chat/conversations/${nc.id}/messages`, { headers: hdrs });
                  setMessages(msgResp.data.messages || []);
                  setReadReceipts(msgResp.data.read_receipts || []);
                } catch (e) { /* ignore */ }
              }
            }
          }
        }

        setConversations(newConvs);
        const total = newConvs.reduce((sum, c) => sum + (c.unread || 0), 0);
        setTotalUnread(total);
        lastPollRef.current = newConvs;

        // Refresh open conversation (messages + read receipts) while visible
        if (activeConvRef.current && document.visibilityState === 'visible') {
          try {
            const mr = await axios.get(`${API_URL}/api/community/chat/conversations/${activeConvRef.current.id}/messages`, { headers: hdrs });
            setMessages(mr.data.messages || []);
            setReadReceipts(mr.data.read_receipts || []);
          } catch (e) { /* ignore */ }
        }
      } catch (e) { console.error('Chat poll error:', e?.response?.status || e?.message); }
    };

    poll(); // Initial poll
    const interval = setInterval(poll, 15000); // Every 15 seconds
    return () => clearInterval(interval);
  }, [user, API_URL]);

  const openConversation = async (conv) => {
    setActiveConv(conv);
    setMessages([]);
    setLoading(true);
    setShowEmojiPicker(false);
    try {
      const hdrs = getHeaders();
      const resp = await axios.get(`${API_URL}/api/community/chat/conversations/${conv.id}/messages`, { headers: hdrs });
      setMessages(resp.data.messages || []);
      setReadReceipts(resp.data.read_receipts || []);
      await axios.post(`${API_URL}/api/community/chat/read/${conv.id}`, {}, { headers: hdrs });
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread: 0 } : c));
      setTotalUnread(prev => Math.max(0, prev - (conv.unread || 0)));
    } catch (e) { console.error(e); }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const sendMessage = async (contentType = 'text', gifUrl = null) => {
    const content = contentType === 'gif' ? (gifUrl || '') : input.trim();
    if (!content || !activeConv) return;
    setSendingMsg(true);
    if (contentType === 'text') setInput('');
    setShowEmojiPicker(false);
    try {
      const hdrs = getHeaders();
      const convId = activeConv.id;
      const sendResp = await axios.post(`${API_URL}/api/community/chat/conversations/${convId}/messages`, {
        content, content_type: contentType, gif_url: gifUrl,
      }, { headers: { ...hdrs, 'Content-Type': 'application/json' } });
      // Optimistically add the sent message to state immediately
      if (sendResp.data.message) {
        setMessages(msgs => [...msgs, sendResp.data.message]);
      }
    } catch (e) { console.error('Send message error:', e); showToast(e?.response?.data?.detail || 'Nachricht konnte nicht gesendet werden'); }
    setSendingMsg(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const startDM = async (otherUser) => {
    try {
      const resp = await axios.post(`${API_URL}/api/community/chat/dm/${otherUser.id}`, {}, { headers: getHeaders() });
      const conv = resp.data.conversation;
      setShowUserSearch(false);
      setSearchQuery('');
      fetchConversations();
      openConversation(conv);
    } catch (e) { console.error(e); }
  };

  const searchUsers = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const resp = await axios.get(`${API_URL}/api/community/chat/users/search?q=${encodeURIComponent(q)}`, { headers: getHeaders() });
      setSearchResults(resp.data.users || []);
    } catch (e) { console.error(e); }
    setSearching(false);
  };

  const updateNotifSetting = async (key, value) => {
    const newSettings = { ...notifSettings, [key]: value };
    setNotifSettings(newSettings);
    try {
      await axios.put(`${API_URL}/api/community/chat/notification-settings`,
        { [key === 'global' ? 'global_enabled' : key]: value },
        { headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
    } catch (e) { console.error(e); }
  };

  // Filter conversations by tab
  const filteredConversations = conversations.filter(c => {
    if (activeTab === 'all') return true;
    if (activeTab === 'dm') return c.type === 'dm';
    if (activeTab === 'groups') return c.type !== 'dm';
    return true;
  });

  // Team conversations that user is member of (not leader)
  const memberTeamConvs = teamConvs.filter(t => t.leader_id !== user?.id);

  // GIF search
  const loadTrendingGifs = async () => {
    setGifLoading(true);
    try {
      const hdrs = getHeaders();
      const resp = await axios.get(`${API_URL}/api/community/gifs/trending`, { headers: hdrs });
      setGifResults(resp.data.gifs || []);
    } catch (e) { console.error(e); }
    setGifLoading(false);
  };

  const searchGifs = (q) => {
    setGifSearch(q);
    if (gifSearchTimer.current) clearTimeout(gifSearchTimer.current);
    if (!q.trim()) { loadTrendingGifs(); return; }
    gifSearchTimer.current = setTimeout(async () => {
      setGifLoading(true);
      try {
        const hdrs = getHeaders();
        const resp = await axios.get(`${API_URL}/api/community/gifs/search?q=${encodeURIComponent(q)}`, { headers: hdrs });
        setGifResults(resp.data.gifs || []);
      } catch (e) { console.error(e); }
      setGifLoading(false);
    }, 400);
  };

  const openGifPicker = () => {
    setShowGifPicker(!showGifPicker);
    setShowEmojiPicker(false);
    if (!showGifPicker) { setGifSearch(''); loadTrendingGifs(); }
  };

  const sendGif = (gif) => {
    sendMessage('gif', gif.url);
    setShowGifPicker(false);
  };

  // File upload for chat
  const handleFileUpload = async (file) => {
    if (!file || !activeConv) return;
    setUploadingFile(true);
    try {
      const hdrs = getHeaders();
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${API_URL}/api/community/chat/upload`, fd, { headers: { ...hdrs, 'Content-Type': 'multipart/form-data' } });
      const isImage = resp.data.file_type?.startsWith('image/');
      await sendMessage(isImage ? 'image' : 'file', null);
      // Send a message with the file info
      const convId = activeConv.id;
      const fileContent = isImage ? `[Bild: ${resp.data.file_name}]` : `[Datei: ${resp.data.file_name}]`;
      const sendResp = await axios.post(`${API_URL}/api/community/chat/conversations/${convId}/messages`, {
        content: fileContent, content_type: isImage ? 'image' : 'file', gif_url: resp.data.file_url,
      }, { headers: { ...hdrs, 'Content-Type': 'application/json' } });
      if (sendResp.data.message) setMessages(msgs => [...msgs, sendResp.data.message]);
    } catch (e) { console.error('Upload error:', e); showToast('Datei konnte nicht hochgeladen werden'); }
    setUploadingFile(false);
    setPastePreview(null);
  };

  // Paste handler for images
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) handleFileUpload(file);
        return;
      }
    }
  };

  // YouTube link preview
  const getYoutubeId = (text) => {
    if (!text) return null;
    const m = text.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    return m ? m[1].substring(0, 11) : null;
  };

  // Group management
  const openGroupSettings = () => {
    if (!activeConv || activeConv.type !== 'custom_group') return;
    setEditGroupName(activeConv.display_name || activeConv.name || '');
    setGroupMemberSearch('');
    setGroupMemberResults([]);
    setShowGroupSettings(true);
  };

  const saveGroupName = async () => {
    if (!activeConv || !editGroupName.trim()) return;
    try {
      const hdrs = getHeaders();
      await axios.put(`${API_URL}/api/community/chat/groups/${activeConv.id}`, { name: editGroupName.trim() }, { headers: { ...hdrs, 'Content-Type': 'application/json' } });
      setActiveConv(prev => ({ ...prev, display_name: editGroupName.trim(), name: editGroupName.trim() }));
      showToast('Gruppenname geändert', 'success');
    } catch (e) { showToast(e?.response?.data?.detail || 'Name konnte nicht geändert werden'); }
  };

  const addGroupMember = async (userId) => {
    if (!activeConv) return;
    try {
      const hdrs = getHeaders();
      await axios.put(`${API_URL}/api/community/chat/groups/${activeConv.id}`, { add_members: [userId] }, { headers: { ...hdrs, 'Content-Type': 'application/json' } });
      setGroupMemberSearch('');
      setGroupMemberResults([]);
      showToast('Mitglied hinzugefügt', 'success');
    } catch (e) { showToast(e?.response?.data?.detail || 'Mitglied konnte nicht hinzugefügt werden'); }
  };

  const removeGroupMember = async (userId) => {
    if (!activeConv) return;
    try {
      const hdrs = getHeaders();
      await axios.put(`${API_URL}/api/community/chat/groups/${activeConv.id}`, { remove_members: [userId] }, { headers: { ...hdrs, 'Content-Type': 'application/json' } });
      showToast('Mitglied entfernt', 'success');
    } catch (e) { showToast(e?.response?.data?.detail || 'Mitglied konnte nicht entfernt werden'); }
  };

  const deleteGroup = async () => {
    if (!activeConv) return;
    if (!(await confirmDialog('Gruppe wirklich löschen? Alle Nachrichten gehen verloren.', { title: 'Gruppe löschen' }))) return;
    try {
      const hdrs = getHeaders();
      await axios.delete(`${API_URL}/api/community/chat/groups/${activeConv.id}`, { headers: hdrs });
      setShowGroupSettings(false);
      setActiveConv(null);
      fetchConversations();
      showToast('Gruppe gelöscht', 'success');
    } catch (e) { showToast(e?.response?.data?.detail || 'Gruppe konnte nicht gelöscht werden'); }
  };

  const searchGroupAddMembers = async (q) => {
    setGroupMemberSearch(q);
    if (q.length < 2) { setGroupMemberResults([]); return; }
    try {
      const hdrs = getHeaders();
      const resp = await axios.get(`${API_URL}/api/community/chat/users/search?q=${encodeURIComponent(q)}`, { headers: hdrs });
      setGroupMemberResults(resp.data.users || []);
    } catch (e) { /* ignore */ }
  };

  const getConvIcon = (type) => {
    if (type === 'wolfpack') return <Shield size={16} className="chat-icon-wolfpack" />;
    if (type === 'team') return <Users size={16} className="chat-icon-team" />;
    if (type === 'community') return <Globe size={16} className="chat-icon-community" />;
    if (type === 'custom_group') return <Users size={16} className="chat-icon-group" />;
    return <MessageSquare size={16} />;
  };

  // Group creation
  const searchGroupMembers = async (q) => {
    setGroupSearchQuery(q);
    if (q.length < 2) { setGroupSearchResults([]); return; }
    try {
      const hdrs = getHeaders();
      const resp = await axios.get(`${API_URL}/api/community/chat/users/search?q=${encodeURIComponent(q)}`, { headers: hdrs });
      setGroupSearchResults((resp.data.users || []).filter(u => !groupMembers.find(m => m.id === u.id)));
    } catch (e) { /* ignore */ }
  };

  const createGroup = async () => {
    if (!groupName.trim()) { showToast('Bitte Gruppenname eingeben'); return; }
    if (groupMembers.length === 0) { showToast('Bitte mindestens ein Mitglied hinzufügen'); return; }
    try {
      const hdrs = getHeaders();
      const resp = await axios.post(`${API_URL}/api/community/chat/groups`, { name: groupName.trim(), member_ids: groupMembers.map(m => m.id) }, { headers: { ...hdrs, 'Content-Type': 'application/json' } });
      setShowCreateGroup(false);
      setGroupName(''); setGroupMembers([]); setGroupSearchQuery(''); setGroupSearchResults([]);
      showToast(`Gruppe "${groupName.trim()}" erstellt`, 'success');
      if (resp.data.group) openConversation({ ...resp.data.group, display_name: resp.data.group.name, type: 'custom_group' });
    } catch (e) { showToast(e?.response?.data?.detail || 'Gruppe konnte nicht erstellt werden'); }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  // Message edit/delete
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editingText, setEditingText] = useState('');

  const within30Min = (msg) => {
    const ts = msg.created_at || '';
    const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(ts);
    return (Date.now() - new Date(hasTz ? ts : ts + 'Z').getTime()) < 30 * 60 * 1000;
  };

  const canManageGroup = isAdminOrCoach || (activeConv?.admin_ids || []).includes(user?.id);

  const startEditMsg = (msg) => { setEditingMsgId(msg.id); setEditingText(msg.content || ''); };

  const saveEditMsg = async () => {
    const text = editingText.trim();
    if (!text || !editingMsgId) return;
    try {
      const resp = await axios.put(`${API_URL}/api/community/chat/messages/${editingMsgId}`, { content: text }, { headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
      const updated = resp.data.message;
      setMessages(prev => prev.map(m => m.id === editingMsgId ? { ...m, ...updated } : m));
      setEditingMsgId(null);
      setEditingText('');
    } catch (e) { showToast(e?.response?.data?.detail || 'Nachricht konnte nicht bearbeitet werden'); }
  };

  const deleteMsg = async (msg) => {
    if (!(await confirmDialog('Nachricht wirklich löschen?', { title: 'Nachricht löschen' }))) return;
    try {
      await axios.delete(`${API_URL}/api/community/chat/messages/${msg.id}`, { headers: getHeaders() });
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, deleted: true, deleted_at: new Date().toISOString(), content: '', gif_url: null } : m));
    } catch (e) { showToast(e?.response?.data?.detail || 'Nachricht konnte nicht gelöscht werden'); }
  };

  const toggleGroupReadOnly = async (value) => {
    if (!activeConv) return;
    try {
      await axios.put(`${API_URL}/api/community/chat/groups/${activeConv.id}`, { read_only: value }, { headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
      setActiveConv(prev => ({ ...prev, read_only: value }));
      showToast(value ? 'Gruppe ist jetzt nur-lesen für Freigeister & Förderer' : 'Alle Mitglieder dürfen wieder schreiben', 'success');
    } catch (e) { showToast(e?.response?.data?.detail || 'Einstellung konnte nicht geändert werden'); }
  };

  const isReadOnlyForMe = activeConv?.read_only && !isAdminOrCoach;

  return (
    <>
      {/* Toggle Button */}
      <button className={`chat-toggle-btn ${totalUnread > 0 ? 'has-unread' : ''}`} onClick={() => setOpen(!open)} data-testid="chat-toggle">
        {open ? <X size={22} /> : <MessageSquare size={22} />}
        {totalUnread > 0 && !open && <span className="chat-badge">{totalUnread}</span>}
      </button>

      {/* Chat Panel */}
      {open && (
        <div className="chat-overlay" data-testid="chat-overlay">
          {/* Toast notifications */}
          {chatToast && (
            <div className={`chat-toast ${chatToast.type === 'success' ? 'chat-toast-success' : 'chat-toast-error'}`} onClick={() => setChatToast(null)}>
              {chatToast.msg}
            </div>
          )}
          {!activeConv ? (
            /* Conversation List */
            <div className="chat-sidebar">
              <div className="chat-sidebar-header">
                <h3>Chat</h3>
                <div className="chat-header-actions">
                  <button className="chat-header-btn" onClick={() => setShowSettings(!showSettings)} title="Benachrichtigungen" data-testid="chat-settings-btn">
                    {notifSettings.global ? <Bell size={16} /> : <BellOff size={16} />}
                  </button>
                  <button className="chat-header-btn" onClick={() => setOpen(false)}><X size={18} /></button>
                </div>
              </div>

              {showSettings && (
                <div className="chat-notif-settings" data-testid="chat-notif-settings">
                  <h4>Benachrichtigungen</h4>
                  {[
                    { key: 'global', label: 'Global an/aus' },
                    { key: 'dm', label: 'Direktnachrichten' },
                    ...(isAdminOrCoach ? [{ key: 'wolfpack', label: 'Wolfsrudel' }] : []),
                    { key: 'team', label: 'Team' },
                    { key: 'community', label: 'Community' },
                  ].map(s => (
                    <label key={s.key} className="chat-notif-toggle">
                      <span>{s.label}</span>
                      <input type="checkbox" checked={notifSettings[s.key] !== false} onChange={e => updateNotifSetting(s.key, e.target.checked)} />
                    </label>
                  ))}
                </div>
              )}

              {/* Tabs */}
              <div className="chat-tabs">
                {[
                  { id: 'all', label: 'Alle' },
                  { id: 'dm', label: '1 zu 1' },
                  { id: 'groups', label: 'Gruppen' },
                ].map(t => (
                  <button key={t.id} className={`chat-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>{t.label}</button>
                ))}
              </div>

              {/* New DM Button */}
              <div style={{display: 'flex', gap: '0.4rem', padding: '0 0.75rem', marginBottom: '0.25rem'}}>
                <button className="chat-new-dm-btn" style={{flex: 1}} onClick={() => { setShowUserSearch(!showUserSearch); setShowCreateGroup(false); }} data-testid="new-dm-btn">
                  <Send size={14} /> Nachricht
                </button>
                <button className="chat-new-dm-btn" style={{flex: 1}} onClick={() => { setShowCreateGroup(!showCreateGroup); setShowUserSearch(false); }} data-testid="new-group-btn">
                  <Users size={14} /> Neue Gruppe
                </button>
              </div>

              {showUserSearch && (
                <div className="chat-user-search">
                  <div className="chat-search-input-wrap">
                    <Search size={14} />
                    <input value={searchQuery} onChange={e => searchUsers(e.target.value)} placeholder="Benutzer suchen..." autoFocus data-testid="chat-user-search" />
                  </div>
                  {searching && <div className="chat-searching"><Loader2 size={14} className="spin" /></div>}
                  {searchResults.map(u => (
                    <button key={u.id} className="chat-user-result" onClick={() => startDM(u)} data-testid={`dm-user-${u.id}`}>
                      {u.profile_image ? <img src={`${API_URL}${u.profile_image}`} alt="" className="chat-avatar-sm" /> : <UserCircle size={28} />}
                      <div><strong>{u.first_name} {u.last_name}</strong><span className="chat-user-role">{getRoleLabel(u.role)}</span></div>
                    </button>
                  ))}
                </div>
              )}

              {/* Create Group Form */}
              {showCreateGroup && (
                <div className="chat-user-search" style={{padding: '0.5rem 0.75rem'}}>
                  <input value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="Gruppenname..." className="chat-text-input" style={{marginBottom: '0.5rem', width: '100%'}} data-testid="group-name-input" />
                  {groupMembers.length > 0 && (
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.5rem'}}>
                      {groupMembers.map(m => (
                        <span key={m.id} style={{fontSize: '0.72rem', padding: '0.2rem 0.5rem', background: 'rgba(200,164,92,0.15)', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '0.2rem', color: '#C8A45C'}}>
                          {m.first_name} <button style={{background: 'none', border: 'none', color: '#D44040', cursor: 'pointer', padding: 0, fontSize: '0.8rem'}} onClick={() => setGroupMembers(prev => prev.filter(p => p.id !== m.id))}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="chat-search-input-wrap">
                    <Search size={14} />
                    <input value={groupSearchQuery} onChange={e => searchGroupMembers(e.target.value)} placeholder="Mitglieder suchen..." data-testid="group-member-search" />
                  </div>
                  {groupSearchResults.map(u => (
                    <button key={u.id} className="chat-user-result" onClick={() => { setGroupMembers(prev => [...prev, u]); setGroupSearchResults(prev => prev.filter(r => r.id !== u.id)); setGroupSearchQuery(''); }}>
                      {u.profile_image ? <img src={`${API_URL}${u.profile_image}`} alt="" className="chat-avatar-sm" /> : <UserCircle size={28} />}
                      <div><strong>{u.first_name} {u.last_name}</strong></div>
                    </button>
                  ))}
                  <button className="chat-new-dm-btn" style={{marginTop: '0.5rem', width: '100%', justifyContent: 'center'}} onClick={createGroup} disabled={!groupName.trim() || groupMembers.length === 0} data-testid="create-group-submit">
                    <Users size={14} /> Gruppe erstellen ({groupMembers.length} Mitglieder)
                  </button>
                </div>
              )}

              {/* Team conversations user is member of */}
              {activeTab !== 'dm' && memberTeamConvs.map(tc => (
                <button key={tc.id} className="chat-conv-item" onClick={() => openConversation({ id: tc.id, type: 'team', display_name: tc.display_name, unread: 0 })} data-testid={`conv-${tc.id}`}>
                  <div className="chat-conv-icon">{getConvIcon('team')}</div>
                  <div className="chat-conv-info">
                    <span className="chat-conv-name">{tc.display_name}</span>
                  </div>
                </button>
              ))}

              {/* Conversation list */}
              {filteredConversations.map(c => (
                <button key={c.id} className={`chat-conv-item ${c.unread > 0 ? 'has-unread' : ''}`} onClick={() => openConversation(c)} data-testid={`conv-${c.id}`}>
                  <div className="chat-conv-icon">
                    {c.type === 'dm' && c.display_image ? <img src={`${API_URL}${c.display_image}`} alt="" className="chat-avatar-sm" /> : getConvIcon(c.type)}
                  </div>
                  <div className="chat-conv-info">
                    <span className="chat-conv-name">{c.display_name}</span>
                    {c.last_message_text && <span className="chat-conv-preview">{c.last_message_author ? `${c.last_message_author}: ` : ''}{c.last_message_text}</span>}
                  </div>
                  <div className="chat-conv-meta">
                    {c.last_message_at && <span className="chat-conv-time">{formatTime(c.last_message_at)}</span>}
                    {c.unread > 0 && <span className="chat-unread-badge">{c.unread}</span>}
                  </div>
                </button>
              ))}

              {filteredConversations.length === 0 && !showUserSearch && (
                <div className="chat-empty">Noch keine Chats. Starte eine neue Nachricht!</div>
              )}
            </div>
          ) : (
            /* Chat Window */
            <div className="chat-window">
              <div className="chat-window-header">
                <button className="chat-back-btn" onClick={() => { setActiveConv(null); setShowGroupSettings(false); fetchConversations(); }} data-testid="chat-back"><ChevronLeft size={18} /></button>
                <div className="chat-window-title">
                  {getConvIcon(activeConv.type)}
                  <span>{activeConv.display_name}</span>
                </div>
                <div style={{display: 'flex', gap: '0.3rem'}}>
                  {activeConv.type === 'custom_group' && (
                    <button className="chat-header-btn" onClick={openGroupSettings} title="Gruppeneinstellungen" data-testid="group-settings-btn"><Settings size={16} /></button>
                  )}
                  <button className="chat-header-btn" onClick={() => setOpen(false)}><X size={18} /></button>
                </div>
              </div>

              {/* Group Settings Panel */}
              {showGroupSettings && activeConv.type === 'custom_group' && (
                <div className="chat-group-settings">
                  {canManageGroup ? (
                    <>
                      <div className="form-group" style={{marginBottom: '0.75rem'}}>
                        <label style={{fontSize: '0.72rem', color: '#8A9AAC'}}>Gruppenname</label>
                        <div style={{display: 'flex', gap: '0.3rem'}}>
                          <input value={editGroupName} onChange={e => setEditGroupName(e.target.value)} className="chat-text-input" style={{flex: 1, borderRadius: '6px', padding: '0.4rem 0.6rem'}} />
                          <button className="chat-input-action" onClick={saveGroupName} title="Speichern" style={{color: '#C8A45C'}}><Edit3 size={16} /></button>
                        </div>
                      </div>
                      <div className="form-group" style={{marginBottom: '0.75rem'}}>
                        <label style={{fontSize: '0.72rem', color: '#8A9AAC'}}>Mitglied hinzufügen</label>
                        <div className="chat-search-input-wrap">
                          <Search size={14} />
                          <input value={groupMemberSearch} onChange={e => searchGroupAddMembers(e.target.value)} placeholder="Benutzer suchen..." />
                        </div>
                        {groupMemberResults.map(u => (
                          <button key={u.id} className="chat-user-result" onClick={() => addGroupMember(u.id)}>
                            <UserPlus size={14} style={{color: '#2D8B4E'}} />
                            <span>{u.first_name} {u.last_name}</span>
                          </button>
                        ))}
                      </div>
                      {isAdminOrCoach && (
                        <label className="chat-readonly-toggle" data-testid="group-readonly-toggle">
                          <span><Lock size={12} style={{marginRight: '0.3rem', verticalAlign: '-2px'}} />Nur Botschafter dürfen schreiben</span>
                          <input type="checkbox" checked={!!activeConv.read_only} onChange={e => toggleGroupReadOnly(e.target.checked)} />
                        </label>
                      )}
                      <button className="chat-new-dm-btn" style={{width: '100%', justifyContent: 'center', color: '#D44040', borderColor: 'rgba(212,64,64,0.3)'}} onClick={deleteGroup}>
                        <Trash2 size={14} /> Gruppe löschen
                      </button>
                    </>
                  ) : (
                    <p style={{fontSize: '0.78rem', color: '#8A9AAC', margin: 0, textAlign: 'center'}} data-testid="group-settings-no-access">Nur Gruppen-Admins können diese Gruppe verwalten.</p>
                  )}
                </div>
              )}

              <div className="chat-messages" data-testid="chat-messages">
                {loading ? (
                  <div className="chat-loading"><Loader2 size={24} className="spin" /></div>
                ) : (
                  <>
                    {messages.length === 0 && <div className="chat-empty-messages">Noch keine Nachrichten. Schreib die erste!</div>}
                    {messages.map(msg => {
                      const isOwn = msg.author_id === user?.id;
                      const canEdit = isOwn && !msg.deleted && within30Min(msg) && msg.content_type === 'text';
                      const canDelete = !msg.deleted && ((isOwn && within30Min(msg)) || isAdminOrCoach);
                      return (
                      <div key={msg.id} className={`chat-msg ${isOwn ? 'own' : ''}`} data-testid={`msg-${msg.id}`}>
                        {!isOwn && (
                          <div className="chat-msg-avatar">
                            {msg.author_image ? <img src={`${API_URL}${msg.author_image}`} alt="" /> : <UserCircle size={28} />}
                          </div>
                        )}
                        <div className="chat-msg-bubble">
                          {(canEdit || canDelete) && editingMsgId !== msg.id && (
                            <div className="chat-msg-actions">
                              {canEdit && <button className="chat-msg-action-btn" onClick={() => startEditMsg(msg)} title="Bearbeiten" data-testid={`edit-msg-${msg.id}`}><Pencil size={12} /></button>}
                              {canDelete && <button className="chat-msg-action-btn danger" onClick={() => deleteMsg(msg)} title="Löschen" data-testid={`delete-msg-${msg.id}`}><Trash2 size={12} /></button>}
                            </div>
                          )}
                          {!isOwn && <span className="chat-msg-author">{msg.author_name}</span>}
                          {msg.deleted ? (
                            <p className="chat-msg-deleted" data-testid={`deleted-msg-${msg.id}`}>🚫 Nachricht gelöscht</p>
                          ) : editingMsgId === msg.id ? (
                            <div className="chat-msg-edit-area" data-testid="chat-msg-edit-area">
                              <textarea className="chat-msg-edit-input" value={editingText} onChange={e => setEditingText(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEditMsg(); } if (e.key === 'Escape') setEditingMsgId(null); }}
                                autoFocus data-testid="chat-msg-edit-input" />
                              <div className="chat-msg-edit-actions">
                                <button className="chat-msg-edit-cancel" onClick={() => setEditingMsgId(null)}>Abbrechen</button>
                                <button className="chat-msg-edit-save" onClick={saveEditMsg} data-testid="chat-msg-edit-save">Speichern</button>
                              </div>
                            </div>
                          ) : msg.content_type === 'gif' ? (
                            <img src={msg.gif_url || msg.content} alt="GIF" className="chat-msg-gif" />
                          ) : msg.content_type === 'image' ? (
                            <img src={`${API_URL}${msg.gif_url}`} alt="Bild" className="chat-msg-image" onClick={() => window.open(`${API_URL}${msg.gif_url}`, '_blank')} />
                          ) : msg.content_type === 'file' ? (
                            <a href={`${API_URL}${msg.gif_url}`} target="_blank" rel="noreferrer" className="chat-msg-file">
                              <Paperclip size={14} /> {msg.content?.replace('[Datei: ', '').replace(']', '') || 'Datei'}
                            </a>
                          ) : (
                            <>
                              <p className="chat-msg-text">{msg.content}</p>
                              {getYoutubeId(msg.content) && (
                                <a href={msg.content.match(/https?:\/\/[^\s]+/)?.[0] || '#'} target="_blank" rel="noreferrer" className="chat-yt-preview">
                                  <img src={`https://img.youtube.com/vi/${getYoutubeId(msg.content)}/mqdefault.jpg`} alt="YouTube" />
                                  <div className="chat-yt-play">▶</div>
                                </a>
                              )}
                            </>
                          )}
                          <span className="chat-msg-time">
                            {formatTime(msg.created_at)}
                            {isOwn && !msg.deleted && (() => {
                              const rc = readReceipts.filter(r => r.last_read && msg.created_at && new Date(r.last_read) >= new Date(msg.created_at)).length;
                              const isGroup = activeConv?.type && activeConv.type !== 'dm';
                              if (rc > 0) return <span className="chat-msg-read read" title={isGroup ? `Gelesen von ${rc}` : 'Gelesen'} data-testid={`read-receipt-${msg.id}`}><CheckCheck size={13} />{isGroup ? <em>{rc}</em> : null}</span>;
                              return <span className="chat-msg-read" title="Zugestellt" data-testid={`read-receipt-${msg.id}`}><Check size={13} /></span>;
                            })()}
                          </span>
                          {msg.deleted && msg.deleted_at && (
                            <span className="chat-msg-meta-label">gelöscht · {formatTime(msg.deleted_at)}</span>
                          )}
                          {!msg.deleted && msg.edited_at && (
                            <span className="chat-msg-meta-label" data-testid={`edited-label-${msg.id}`}>bearbeitet · {formatTime(msg.edited_at)}</span>
                          )}
                        </div>
                      </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Input */}
              {isReadOnlyForMe ? (
                <div className="chat-readonly-note" data-testid="chat-readonly-note">
                  <Lock size={14} /> In dieser Gruppe können nur Botschafter schreiben
                </div>
              ) : (
              <div className="chat-input-area">
                {showEmojiPicker && (
                  <div className="chat-emoji-picker" data-testid="chat-emoji-picker">
                    <div className="chat-emoji-cats">
                      {Object.keys(EMOJI_CATS).map(cat => (
                        <button key={cat} className={`chat-emoji-cat ${emojiCat === cat ? 'active' : ''}`} onClick={() => setEmojiCat(cat)}>{cat}</button>
                      ))}
                    </div>
                    <div className="chat-emoji-grid">
                      {EMOJI_CATS[emojiCat].map(em => (
                        <button key={em} className="chat-emoji-btn" onClick={() => { setInput(prev => prev + em); setShowEmojiPicker(false); inputRef.current?.focus(); }}>{em}</button>
                      ))}
                    </div>
                  </div>
                )}
                {showGifPicker && (
                  <div className="chat-gif-picker" data-testid="chat-gif-picker">
                    <div className="chat-gif-search-wrap">
                      <Search size={14} />
                      <input value={gifSearch} onChange={e => searchGifs(e.target.value)} placeholder="GIFs suchen..." autoFocus data-testid="gif-search-input" />
                      {gifSearch && <button className="chat-gif-clear" onClick={() => { setGifSearch(''); loadTrendingGifs(); }}><X size={12} /></button>}
                    </div>
                    <div className="chat-gif-grid">
                      {gifLoading ? (
                        <div className="chat-gif-loading"><Loader2 size={20} className="spin" /></div>
                      ) : gifResults.length === 0 ? (
                        <div className="chat-gif-empty">Keine GIFs gefunden</div>
                      ) : (
                        gifResults.map(gif => (
                          <button key={gif.id} className="chat-gif-item" onClick={() => sendGif(gif)} title={gif.title} data-testid={`gif-${gif.id}`}>
                            <img src={gif.preview || gif.url} alt={gif.title} loading="lazy" />
                          </button>
                        ))
                      )}
                    </div>
                    <div className="chat-gif-powered">Powered by GIPHY</div>
                  </div>
                )}
                <div className="chat-input-row">
                  <button className="chat-input-action" onClick={() => { setShowEmojiPicker(!showEmojiPicker); setShowGifPicker(false); }} title="Emoji" data-testid="chat-emoji-btn"><Smile size={20} /></button>
                  <button className={`chat-input-action ${showGifPicker ? 'active' : ''}`} onClick={openGifPicker} title="GIF" data-testid="chat-gif-btn"><span className="gif-label">GIF</span></button>
                  <button className="chat-input-action" onClick={() => fileInputRef.current?.click()} title="Datei anhängen" data-testid="chat-attach-btn">
                    {uploadingFile ? <Loader2 size={18} className="spin" /> : <Paperclip size={20} />}
                  </button>
                  <input ref={fileInputRef} type="file" hidden onChange={async e => { const f = e.target.files[0]; const input = e.target; if (f) await handleFileUpload(f); input.value = ''; }} />
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder="Nachricht schreiben..."
                    rows={1}
                    className="chat-text-input"
                    data-testid="chat-input"
                  />
                  <button className="chat-send-btn" onClick={() => sendMessage()} disabled={(!input.trim() && !uploadingFile) || sendingMsg} data-testid="chat-send">
                    {sendingMsg ? <Loader2 size={18} className="spin" /> : <Send size={18} />}
                  </button>
                </div>
              </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
